import React, { useState } from "react";
import { toast } from "sonner";
import { Palette, AlertTriangle, ImagePlus, X, Loader2 } from "lucide-react";
import { API, authFetch } from "@/App";
import { AuthImage } from "./AuthImage";
import { FabricCard } from "./FabricCard";
import { FilePickButton } from "./FilePickButton";
import { FabricReviewModal } from "./FabricReviewModal";
import { refreshFabricLibrary, attachmentImageUrl, responseError } from "./useFabricLibrary";
import { extractEmailImageCandidates } from "./emailImages";
import { parseEmailFile, isEmailFile } from "./emailFile";
import { postSwatch } from "./swatchUpload";
import { FabricCodePicker } from "./FabricCodePicker";
import { parseManualFabrics } from "./fabricUtils";

// Fabric section of the CONTENT popup: detected fabrics, photos, quick add and email import.
export function FabricPanel({ orderId, orderName, fabric, onPhotosChanged, manualFabrics, onSetManualFabrics }) {
  const { resolved, reload, library } = fabric;
  const manualKeys = parseManualFabrics(manualFabrics).map((r) => r.key);
  const existingKeys = new Set(resolved.fabrics.map((f) => f.key));
  const addManual = (key) => onSetManualFabrics?.(orderId, [...new Set([...manualKeys, key])]);
  const removeManual = (key) => onSetManualFabrics?.(orderId, manualKeys.filter((k) => k !== key));
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);

  const syncPhotos = async () => {
    const list = await reload();
    onPhotosChanged?.(orderId, list);
  };

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const uploadToLibrary = (f, file) => run(async () => {
    try { await postSwatch(f.collection, f.code, file); } catch (e) { toast.error(e.message); return; }
    await refreshFabricLibrary();
    toast.success(`Photo enregistrée dans la bibliothèque : ${f.label}`);
  });

  const uploadOrderPhoto = (file, f) => run(async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("print_with_sheet", "true");
    if (f) { fd.append("fabric_key", f.key); fd.append("fabric_label", f.label); }
    const res = await authFetch(`${API}/orders/${orderId}/attachments`, { method: "POST", body: fd });
    if (!res.ok) { toast.error(await responseError(res, "Erreur d'upload")); return; }
    await syncPhotos();
    toast.success("Photo ajoutée à la commande (imprimée avec la fiche)");
  });

  const removeFromSheet = (photo) => run(async () => {
    const res = await authFetch(`${API}/orders/${orderId}/attachments/${photo.attachment_id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ print_with_sheet: false }),
    });
    if (!res.ok) { toast.error(await responseError(res)); return; }
    await syncPhotos();
    toast.success("La photo ne sera plus imprimée (elle reste dans les pièces jointes)");
  });

  const importEmail = (file) => run(async () => {
    try {
      const data = await parseEmailFile(file);
      const candidates = extractEmailImageCandidates(data.html, data.images);
      if (!candidates.items.length) { toast.info("Aucune image trouvée dans cet email"); return; }
      setReview(candidates);
    } catch (e) { toast.error(e.message); }
  });

  const onGeneralFile = (file) => (isEmailFile(file.name) ? importEmail(file) : uploadOrderPhoto(file, null));

  return (
    <section className="mt-5 border-t border-zinc-200 pt-4" data-testid="fabric-panel">
      <div className="flex items-center justify-between mb-3 gap-3">
        <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
          <Palette className="w-4 h-4 text-orange-500" />Toiles de la commande
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-500" />}
        </h4>
        <FilePickButton accept="image/*,.eml,.msg" onFile={onGeneralFile} disabled={busy} testId="fabric-order-photo-btn" className="h-8 text-xs gap-1.5" title="Image de la toile, ou fichier email (.eml / .msg) pour récupérer ses images">
          <ImagePlus className="w-3.5 h-3.5" />Photo toile
        </FilePickButton>
      </div>

      {resolved.alert && (
        <div className="flex items-start gap-2 p-2.5 mb-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="fabric-panel-alert">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-500" /><span>{resolved.alert}</span>
        </div>
      )}

      {resolved.fabrics.length === 0 ? (
        <p className="text-xs text-zinc-500" data-testid="fabric-panel-empty">Aucune référence de toile détectée dans le texte : saisissez le code de la toile ci-dessous.</p>
      ) : (
        <div className="space-y-2">
          {resolved.fabrics.map((f, i) => (
            <FabricCard key={f.key} idx={i} orderId={orderId} fabric={f} busy={busy}
              onLibraryFile={(file) => uploadToLibrary(f, file)} onOrderFile={(file) => uploadOrderPhoto(file, f)} onRemoveOrderPhoto={removeFromSheet} onRemoveManual={removeManual} />
          ))}
        </div>
      )}

      {onSetManualFabrics && (
        <div className="mt-3">
          <FabricCodePicker library={library} existingKeys={existingKeys} onPick={addManual} testId="fabric-panel-picker" />
        </div>
      )}

      {resolved.generalPhotos.length > 0 && (
        <div className="mt-3" data-testid="fabric-general-photos">
          <p className="text-[11px] font-semibold text-zinc-500 mb-1.5">Photos jointes à la fiche</p>
          <div className="flex flex-wrap gap-2">
            {resolved.generalPhotos.map((p) => (
              <div key={p.attachment_id} className="relative">
                <AuthImage src={attachmentImageUrl(orderId, p)} alt={p.original_filename} className="w-24 h-14 object-cover rounded-md border border-zinc-200" />
                <button type="button" onClick={() => removeFromSheet(p)} className="absolute -top-1.5 -right-1.5 bg-white border border-zinc-300 rounded-full p-0.5 hover:bg-red-50" title="Ne plus imprimer avec la fiche" data-testid={`fabric-general-remove-${p.attachment_id}`}>
                  <X className="w-3 h-3 text-red-500" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {review && (
        <FabricReviewModal open orderId={orderId} orderName={orderName} candidates={review}
          onClose={() => setReview(null)} onSaved={syncPhotos} />
      )}
    </section>
  );
}
